import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Snackbar,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import "../styles/global.css";
import { useDispatch, useSelector } from "react-redux";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { fetchADUserThunk, fetchDguRemoteIPThunk, getClientIPThunk, login_thunk } from "../store/redux/thunk";

const LoginScreen = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false); 
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { dguRemoteIP, adUser, clientIP } = useSelector((state) => state.filter);

  const validateUser = useCallback(() => {
    console.log("Validation started...");

    if (isValidating) return;
    setIsValidating(true);

    if (!adUser?.data?.length || !dguRemoteIP?.data?.length || !clientIP) {
      console.log("Validation failed: Missing required data");
      console.log("adUser:", adUser);
      console.log("dguRemoteIP:", dguRemoteIP);
      console.log("clientIP:", clientIP);
      setSnackbarMessage("Validation failed: Missing required data");
      setSnackbarOpen(true);
      setIsValidating(false);
      return;
    }

    const user = adUser.data[0];
    console.log("Extracted User:", user);

    if (user?.dguIsmacaddress) {
      console.log("MAC address validation is enabled for this user.");

      const isValidIP = dguRemoteIP.data.some((ipData) => {
        console.log("Checking IP:", ipData._identifier, "against clientIP:", clientIP);
        return ipData._identifier === clientIP;
      });

      if (isValidIP) {
        console.log("User validated successfully! IP matched.");
        navigate("/menu");
      } else {
        console.log("User validation failed. IP mismatch.");
        setSnackbarMessage("User validation failed. IP mismatch.");
        setSnackbarOpen(true);
        setLoading(false);
        setIsValidating(false);
      }
    } else {
      console.log("MAC address validation not required for this user.");
      // navigate("/menu");
    }
    setIsValidating(false);
  }, [adUser, dguRemoteIP, clientIP, navigate, isValidating]);

  useEffect(() => {
    if (adUser && dguRemoteIP && clientIP && !isValidating) {
      validateUser();
    }
  }, [adUser, dguRemoteIP, clientIP, validateUser, isValidating]);

  const handleLogin = () => {
    setUsernameError(false);
    setPasswordError(false);
    
    let hasError = false;
    if (!username.trim()) {
      setUsernameError(true);
      hasError = true;
    }
    if (!password.trim()) {
      setPasswordError(true);
      hasError = true;
    }
    
    if (hasError) {
      setSnackbarMessage("Username and password are required.");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    setSnackbarOpen(false);
    
    dispatch(login_thunk({ User: username, pass: password }))
      .then(async (response) => {
        console.log("Login response:", response);
        
        if (response?.status === "OK" && response?.data?.length > 0) {
          const userData = response.data[0];
          
          await AsyncStorage.setItem("username", username);
          await AsyncStorage.setItem("password", password);
          await AsyncStorage.setItem("userId", userData.userid);

          dispatch(getClientIPThunk());
          dispatch(fetchDguRemoteIPThunk(userData.userid));
          dispatch(fetchADUserThunk(userData.userid));
        } else {
          setLoading(false);
          setSnackbarMessage(response?.message || "Invalid Credentials. Try again.");
          setSnackbarOpen(true);
          setPassword("");
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Login error:", error);
        setSnackbarMessage("Network error. Try again.");
        setSnackbarOpen(true);
        setPassword("");
      });
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>Login</h2>
        <p className="login-subtitle">To access your account</p>

        <TextField
          label="Username"
          variant="outlined"
          fullWidth
          error={usernameError}
          helperText={usernameError ? "Username is required" : ""}
          value={username}
          onChange={(e) => {
            setUsernameError(false);
            setUsername(e.target.value);
          }}
          onKeyPress={handleKeyPress}
          className="login-input"
          disabled={isLoading}
          autoFocus
        />

        <TextField
          label="Password"
          variant="outlined"
          fullWidth
          type={passwordVisible ? "text" : "password"}
          error={passwordError}
          helperText={passwordError ? "Password is required" : ""}
          value={password}
          onChange={(e) => {
            setPasswordError(false);
            setPassword(e.target.value);
          }}
          onKeyPress={handleKeyPress}
          className="login-input"
          disabled={isLoading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  edge="end"
                >
                  {passwordVisible ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="contained"
          color="primary"
          fullWidth
          className="login-button"
          onClick={handleLogin}
          disabled={isLoading}
        >
          {isLoading ? "Logging in..." : "Login"}
        </Button>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        action={
          <Button
            color="secondary"
            size="small"
            onClick={() => setSnackbarOpen(false)}
          >
            Dismiss
          </Button>
        }
      />
    </div>
  );
};

export default LoginScreen;





// import React, { useEffect, useState, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//   TextField,
//   Button,
//   Snackbar,
//   IconButton,
//   InputAdornment,
// } from "@mui/material";
// import { Visibility, VisibilityOff } from "@mui/icons-material";
// import "../styles/global.css";
// import { useDispatch, useSelector } from "react-redux";
// import AsyncStorage from "@react-native-async-storage/async-storage";
// import { fetchADUserThunk, fetchDguRemoteIPThunk, getClientIPThunk, login_thunk } from "../store/redux/thunk";

// const LoginScreen = () => {
//   const [username, setUsername] = useState("");
//   const [password, setPassword] = useState("");
//   const [passwordVisible, setPasswordVisible] = useState(false);
//   const [usernameError, setUsernameError] = useState(false);
//   const [passwordError, setPasswordError] = useState(false);
//   const [snackbarOpen, setSnackbarOpen] = useState(false);
//   const [snackbarMessage, setSnackbarMessage] = useState("");
//   const [isLoading, setLoading] = useState(false);
//   const [isValidating, setIsValidating] = useState(false); 
//   const navigate = useNavigate();
//   const dispatch = useDispatch();


//   const { dguRemoteIP, adUser, clientIP } = useSelector((state) => state.filter);

//   const validateUser = useCallback(() => {
//     console.log("Validation started...");

 
//     if (isValidating) return;
//     setIsValidating(true);

//     if (!adUser?.data?.length || !dguRemoteIP?.data?.length || !clientIP) {
//       console.log("Validation failed: Missing required data");
//       console.log("adUser:", adUser);
//       console.log("dguRemoteIP:", dguRemoteIP);
//       console.log("clientIP:", clientIP);
//       setSnackbarMessage("Validation failed: Missing required data");
//       setSnackbarOpen(true);
//       setIsValidating(false);
//       return;
//     }

//     const user = adUser.data[0];
//     console.log("Extracted User:", user);

//     if (user?.dguIsmacaddress) {
//       console.log("MAC address validation is enabled for this user.");

//       const isValidIP = dguRemoteIP.data.some((ipData) => {
//         console.log("Checking IP:", ipData._identifier, "against clientIP:", clientIP);
//         return ipData._identifier === clientIP;
//       });

//       if (isValidIP) {
//         console.log("User validated successfully! IP matched.");
//         navigate("/menu");
//       } else {
//         console.log("User validation failed. IP mismatch.");
//         setSnackbarMessage("User validation failed. IP mismatch.");
//         setSnackbarOpen(true);

//         setLoading(false);
//         setIsValidating(false);
//       }
//     } else {
//       console.log("MAC address validation not required for this user.");
//       navigate("/menu");
     
//     }
//     setIsValidating(false);
//   }, [adUser, dguRemoteIP, clientIP, navigate, isValidating]);

//   useEffect(() => {
//     if (adUser && dguRemoteIP && clientIP && !isValidating) {
//       validateUser();
//     }
//   }, [adUser, dguRemoteIP, clientIP, validateUser, isValidating]);

//   const handleLogin = () => {
  
//     setUsernameError(false);
//     setPasswordError(false);
    

//     let hasError = false;
//     if (!username.trim()) {
//       setUsernameError(true);
//       hasError = true;
//     }
//     if (!password.trim()) {
//       setPasswordError(true);
//       hasError = true;
//     }
    
//     if (hasError) {
//       setSnackbarMessage("Username and password are required.");
//       setSnackbarOpen(true);
//       return;
//     }

//     setLoading(true);
//     setSnackbarOpen(false);
    
//     dispatch(login_thunk({ User: username, pass: password }))
//       .then(async (response) => {
//         console.log("Login response:", response);
        
//         if (response?.status === "OK" && response?.data?.length > 0) {
//           const userData = response.data[0];
          
       
//           await AsyncStorage.setItem("username", username);
//           await AsyncStorage.setItem("password", password);
//           await AsyncStorage.setItem("userId", userData.userid);

     
//           dispatch(getClientIPThunk());
//           dispatch(fetchDguRemoteIPThunk(userData.userid));
//           dispatch(fetchADUserThunk(userData.userid));
          
      
//         } else {
//           setLoading(false);
//           setSnackbarMessage(response?.message || "Invalid Credentials. Try again.");
//           setSnackbarOpen(true);
//           setPassword("");
//         }
//       })
//       .catch((error) => {
//         setLoading(false);
//         console.error("Login error:", error);
//         setSnackbarMessage("Network error. Try again.");
//         setSnackbarOpen(true);
//         setPassword("");
//       });
//   };


//   const handleKeyPress = (event) => {
//     if (event.key === 'Enter') {
//       handleLogin();
//     }
//   };

//   return (
//     <div className="login-container">
//       <div className="login-box">
//         <h2>Login</h2>
//         <p className="login-subtitle">To access your account</p>

//         <TextField
//           label="Username"
//           variant="outlined"
//           fullWidth
//           error={usernameError}
//           helperText={usernameError ? "Username is required" : ""}
//           value={username}
//           onChange={(e) => {
//             setUsernameError(false);
//             setUsername(e.target.value);
//           }}
//           onKeyPress={handleKeyPress}
//           className="login-input"
//           disabled={isLoading}
//           autoFocus
//         />

//         <TextField
//           label="Password"
//           variant="outlined"
//           fullWidth
//           type={passwordVisible ? "text" : "password"}
//           error={passwordError}
//           helperText={passwordError ? "Password is required" : ""}
//           value={password}
//           onChange={(e) => {
//             setPasswordError(false);
//             setPassword(e.target.value);
//           }}
//           onKeyPress={handleKeyPress}
//           className="login-input"
//           disabled={isLoading}
//           InputProps={{
//             endAdornment: (
//               <InputAdornment position="end">
//                 <IconButton
//                   onClick={() => setPasswordVisible(!passwordVisible)}
//                   edge="end"
//                 >
//                   {passwordVisible ? <VisibilityOff /> : <Visibility />}
//                 </IconButton>
//               </InputAdornment>
//             ),
//           }}
//         />

//         <Button
//           variant="contained"
//           color="primary"
//           fullWidth
//           className="login-button"
//           onClick={handleLogin}
//           disabled={isLoading}
//         >
//           {isLoading ? "Logging in..." : "Login"}
//         </Button>
//       </div>

   
//       <Snackbar
//         open={snackbarOpen}
//         autoHideDuration={4000}
//         onClose={() => setSnackbarOpen(false)}
//         message={snackbarMessage}
//         action={
//           <Button
//             color="secondary"
//             size="small"
//             onClick={() => setSnackbarOpen(false)}
//           >
//             Dismiss
//           </Button>
//         }
//       />
//     </div>
//   );
// };

// export default LoginScreen;




 // const validateUser = () => {
  //   if (!adUser?.data?.length || !dguRemoteIP?.data?.length || !clientIP) {
  //     console.log("Validation failed: Missing required data");
  //     setSnackbarMessage("Validation failed: Missing required data");
  //     setSnackbarOpen(true);
  //     return;
  //   }
  
  //   const user = adUser.data[0]; // Extract first user object
  //   console.log("Extracted User:", user);
  
  //   if (user?.dguIsmacaddress) {
  //     const isValidIP = dguRemoteIP.data.some((ipData) => {
  //       return ipData._identifier === clientIP;
  //     });
  
  //     if (isValidIP) {
  //       console.log("User validated successfully!");
  //       navigate("/menu");
  //     } else {
  //       // navigate("/menu");
  //       setSnackbarMessage("User validation failed. IP mismatch.");
  //       setSnackbarOpen(true);
  //     }
  //   } else {
  //     // navigate("/menu");
  //     setSnackbarMessage("User validation failed. IP mismatch.");
  //     setSnackbarOpen(true);
  //   }
  // };



// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//   TextField,
//   Button,
//   Snackbar,
//   IconButton,
//   InputAdornment,
// } from "@mui/material";
// import { Visibility, VisibilityOff } from "@mui/icons-material";
// import "../styles/global.css";
// import { login_thunk } from "../store/redux/thunk";
// import { useDispatch, useSelector } from "react-redux";
// import AsyncStorage from "@react-native-async-storage/async-storage";

// const LoginScreen = () => {
//   const [username, setUsername] = useState("");
//   const [password, setPassword] = useState("");
//   const [passwordVisible, setPasswordVisible] = useState(false);
//   const [usernameError, setUsernameError] = useState(false);
//   const [passwordError, setPasswordError] = useState(false);
//   const [snackbarOpen, setSnackbarOpen] = useState(false);
//   const [isloading, setLoading] = useState(false);
//   const navigate = useNavigate();
//   const dispatch = useDispatch();

//   // const { dguRemoteIP, adUser, clientIP } = useSelector((state) => state.filter);

//   const handleLogin = () => {
//     setUsernameError(!username);
//     setPasswordError(!password);

//     if (!username || !password) return;
//     console.log("Logging in with:");
//     console.log("Username:", username);
//     console.log("Password:", password);

//     setLoading(true);

//     dispatch(login_thunk({ User: username, pass: password }))
//       .then(async (response) => {
//         setLoading(false);

//         console.log("API Response:", response);

//         if (response?.status === "OK" && response?.data?.length > 0) {
//           const userData = response.data[0];
//           console.log("Extracted User:", userData);
//           await AsyncStorage.setItem("username", username);
//           await AsyncStorage.setItem("password", password);
//           await AsyncStorage.setItem("userId", userData.userid);
//           navigate("/menu");
//         } else {
//           setSnackbarOpen(true);
//         }
//       })
//       .catch((error) => {
//         setLoading(false);
//         console.error("Login error:", error);
//         setSnackbarOpen(true);
//       });
//   };

//   return (
//     <div className="login-container">
//       <div className="login-box">
//         <h2>Login</h2>
//         <p className="login-subtitle">To access your account</p>

//         <TextField
//           label="Username"
//           variant="outlined"
//           fullWidth
//           error={usernameError}
//           helperText={usernameError ? "Username is required" : ""}
//           value={username}
//           onChange={(e) => {
//             setUsernameError(false);
//             setUsername(e.target.value);
//           }}
//           className="login-input"
//         />

//         <TextField
//           label="Password"
//           variant="outlined"
//           fullWidth
//           type={passwordVisible ? "text" : "password"}
//           error={passwordError}
//           helperText={passwordError ? "Password is required" : ""}
//           value={password}
//           onChange={(e) => {
//             setPasswordError(false);
//             setPassword(e.target.value);
//           }}
//           className="login-input"
//           InputProps={{
//             endAdornment: (
//               <InputAdornment position="end">
//                 <IconButton
//                   onClick={() => setPasswordVisible(!passwordVisible)}
//                 >
//                   {passwordVisible ? <VisibilityOff /> : <Visibility />}
//                 </IconButton>
//               </InputAdornment>
//             ),
//           }}
//         />

//         <Button
//           variant="contained"
//           color="primary"
//           fullWidth
//           className="login-button"
//           onClick={handleLogin}
//           disabled={isloading}
//         >
//           {isloading ? "Logging in..." : "Login"}
//         </Button>
//       </div>

//       {/* Snackbar for login failure */}
//       <Snackbar
//         open={snackbarOpen}
//         autoHideDuration={3000}
//         onClose={() => setSnackbarOpen(false)}
//         message="Invalid Credentials. Try again."
//         action={
//           <Button
//             color="secondary"
//             size="small"
//             onClick={() => setSnackbarOpen(false)}
//           >
//             Dismiss
//           </Button>
//         }
//       />
//     </div>
//   );
// };

// export default LoginScreen;
